/**
 * Heal Foods — Shopify Theme JavaScript
 * Handles 3D carton physics, interactive gallery, accessible AJAX cart drawer, and viewport animations.
 */

(function() {
  "use strict";

  var RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------- Currency & State ---------------- */
  window.HealState = {
    cur: "INR",
    cart: null
  };

  try {
    var storedCur = localStorage.getItem("hf_cur");
    if (storedCur) window.HealState.cur = storedCur;
  } catch(e) {}

  function updateCurrencyButtons() {
    var btns = document.querySelectorAll("[data-cur]");
    btns.forEach(function(b) {
      b.setAttribute("aria-pressed", b.getAttribute("data-cur") === window.HealState.cur ? "true" : "false");
    });
  }

  function initCurrencySwitcher() {
    document.addEventListener("click", function(e) {
      var btn = e.target.closest("[data-cur]");
      if (!btn) return;
      var cur = btn.getAttribute("data-cur");
      if (!cur) return;
      window.HealState.cur = cur;
      try { localStorage.setItem("hf_cur", cur); } catch(err) {}
      updateCurrencyButtons();
      var form = document.querySelector('form[action*="/localization"]');
      if (form) {
        var input = form.querySelector('input[name="currency_code"]');
        if (input) {
          input.value = cur;
          form.submit();
        }
      }
    });
    updateCurrencyButtons();
  }

  /* ---------------- Navigation & Sticky Header ---------------- */
  function initHeader() {
    var hdr = document.getElementById("hdr");
    var burger = document.getElementById("burger");
    var nav = document.getElementById("nav");

    window.addEventListener("scroll", function() {
      if (!hdr) return;
      if (window.scrollY > 20) {
        hdr.classList.add("stuck");
      } else {
        hdr.classList.remove("stuck");
      }
    }, { passive: true });

    if (burger && nav) {
      burger.addEventListener("click", function() {
        var isOpen = nav.classList.toggle("open");
        burger.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });
      document.addEventListener("click", function(e) {
        if (!burger.contains(e.target) && !nav.contains(e.target) && nav.classList.contains("open")) {
          nav.classList.remove("open");
          burger.setAttribute("aria-expanded", "false");
        }
      });
    }
  }

  /* ---------------- Scroll Reveal & Word Rise Animations ---------------- */
  function initScrollReveals() {
    // Immediately reveal above-the-fold hero elements
    document.querySelectorAll(".hero [data-rev], .hero [data-mask]").forEach(function(el) {
      el.classList.add("in");
    });

    if (RM) {
      document.querySelectorAll("[data-rev], [data-mask]").forEach(function(el) {
        el.classList.add("in");
      });
      return;
    }

    // Immediately reveal any element already in the initial viewport on load
    var vh = window.innerHeight || document.documentElement.clientHeight;
    document.querySelectorAll("[data-rev], [data-mask]").forEach(function(el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < vh + 50) {
        el.classList.add("in");
      }
    });

    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: "0px 0px -20px 0px", threshold: 0.05 });

      document.querySelectorAll("[data-rev]:not(.in), [data-mask]:not(.in)").forEach(function(el) {
        observer.observe(el);
      });
    } else {
      document.querySelectorAll("[data-rev], [data-mask]").forEach(function(el) {
        el.classList.add("in");
      });
    }
  }

  /* ---------------- 3D Drag-To-Rotate Carton Physics ---------------- */
  function initHero3D() {
    var stage = document.getElementById("stage");
    var pack = document.getElementById("pack");
    var box = document.getElementById("box3d");
    var hint = document.getElementById("boxhint");
    var shadow = stage ? stage.querySelector(".box-shadow") : null;

    if (!stage || !box || RM) return;

    var rotY = -22, rotX = 4, vY = 0, dragging = false, touched = false, lastX = 0;
    var sy = 0, offscreen = false;

    var fcF = box.querySelector(".fc.f"),
        fcR = box.querySelector(".fc.r"),
        fcB = box.querySelector(".fc.b"),
        fcL = box.querySelector(".fc.l");

    box.addEventListener("pointerdown", function(e) {
      dragging = true; touched = true; lastX = e.clientX; vY = 0;
      box.setPointerCapture(e.pointerId);
      box.classList.add("dragging");
      if (hint) hint.classList.add("gone");
    });

    box.addEventListener("pointermove", function(e) {
      if (!dragging) return;
      var dx = e.clientX - lastX;
      lastX = e.clientX;
      rotY += dx * 0.55;
      vY = dx * 0.55;
    });

    ["pointerup", "pointercancel", "lostpointercapture"].forEach(function(ev) {
      box.addEventListener(ev, function() {
        dragging = false;
        box.classList.remove("dragging");
      });
    });

    box.addEventListener("keydown", function(e) {
      if (e.key === "ArrowLeft") { rotY -= 90; touched = true; if (hint) hint.classList.add("gone"); }
      if (e.key === "ArrowRight") { rotY += 90; touched = true; if (hint) hint.classList.add("gone"); }
    });

    stage.addEventListener("mousemove", function(e) {
      var r = stage.getBoundingClientRect();
      rotX = 4 - ((e.clientY - r.top - r.height / 2) / r.height) * 12;
    });

    stage.addEventListener("mouseleave", function() { rotX = 4; });

    function updateFaceLighting(el, offsetRad, baseRad, lightAngle) {
      if (!el) return;
      var diff = Math.cos(baseRad + offsetRad - lightAngle);
      var glare = Math.max(0, diff) * 0.16;
      var shade = Math.max(0, -diff) * 0.32 + 0.04;
      el.style.setProperty("--gl", glare.toFixed(3));
      el.style.setProperty("--sh", shade.toFixed(3));
    }

    var lastTime = performance.now();
    function animate(now) {
      var dt = Math.min(60, now - lastTime);
      lastTime = now;

      var r = stage.getBoundingClientRect();
      sy = Math.max(0, -r.top);
      offscreen = r.bottom < -40;

      if (!offscreen) {
        if (!dragging) {
          if (!touched) {
            rotY += dt * 0.011;
          } else {
            rotY += vY;
            vY *= 0.94;
            if (Math.abs(vY) < 0.14) {
              var snap = Math.round(rotY / 90) * 90;
              rotY += (snap - rotY) * 0.07;
            }
          }
        }

        var out = Math.min(1, sy / (window.innerHeight * 0.9));
        var floaty = Math.sin(now / 2400) * 7;
        if (pack) {
          pack.style.transform = "translate3d(0," + (floaty - out * 84) + "px,0) scale(" + (1 - out * 0.1) + ")";
          pack.style.opacity = String(1 - out * 0.55);
        }
        box.style.transform = "rotateX(" + rotX + "deg) rotateY(" + rotY + "deg)";

        var baseRad = (rotY * Math.PI) / 180;
        var lightAngle = -Math.PI / 4;
        updateFaceLighting(fcF, 0, baseRad, lightAngle);
        updateFaceLighting(fcR, Math.PI / 2, baseRad, lightAngle);
        updateFaceLighting(fcB, Math.PI, baseRad, lightAngle);
        updateFaceLighting(fcL, 3 * Math.PI / 2, baseRad, lightAngle);

        if (shadow) {
          var shadowScale = (1 - floaty * 0.02) * (1 - out * 0.3);
          var shadowW = 0.85 + 0.25 * Math.abs(Math.cos(baseRad));
          var shadowOp = Math.max(0, (0.72 - floaty * 0.025) * (1 - out * 0.8));
          shadow.style.transform = "translateX(-50%) scale(" + (shadowScale * shadowW).toFixed(3) + ", " + shadowScale.toFixed(3) + ")";
          shadow.style.opacity = shadowOp.toFixed(3);
        }
      }
      requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
  }

  /* ---------------- Ambient Husk Particles ---------------- */
  function initHusk() {
    var cv = document.getElementById("husk");
    if (!cv || RM) return;
    var ctx = cv.getContext("2d");
    var flecks = [];

    function resize() {
      var r = cv.parentElement.getBoundingClientRect();
      cv.width = r.width;
      cv.height = r.height;
    }
    window.addEventListener("resize", resize);
    resize();

    for (var i = 0; i < 28; i++) {
      flecks.push({
        x: Math.random() * cv.width,
        y: Math.random() * cv.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: -0.15 - Math.random() * 0.3,
        r: 1 + Math.random() * 1.8,
        a: 0.15 + Math.random() * 0.35
      });
    }

    function render() {
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.fillStyle = "#D4A343";
      for (var j = 0; j < flecks.length; j++) {
        var f = flecks[j];
        f.x += f.vx;
        f.y += f.vy;
        if (f.y < -10) { f.y = cv.height + 10; f.x = Math.random() * cv.width; }
        if (f.x < -10) f.x = cv.width + 10;
        if (f.x > cv.width + 10) f.x = -10;
        ctx.globalAlpha = f.a;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      requestAnimationFrame(render);
    }
    requestAnimationFrame(render);
  }

  /* ---------------- Accessible AJAX Cart Drawer ---------------- */
  var lastFocusedElement = null;

  function handleDrawerKeydown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      window.HealCart.close();
      return;
    }

    if (e.key === "Tab") {
      var drawer = document.getElementById("drawer");
      if (!drawer || !drawer.classList.contains("open")) return;
      var focusables = drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var focusableArr = Array.prototype.filter.call(focusables, function(el) {
        return el.offsetParent !== null && !el.disabled;
      });

      if (focusableArr.length === 0) return;
      var firstEl = focusableArr[0];
      var lastEl = focusableArr[focusableArr.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl || !drawer.contains(document.activeElement)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl || !drawer.contains(document.activeElement)) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
  }

  window.HealCart = {
    open: function() {
      var drawer = document.getElementById("drawer");
      var scrim = document.getElementById("scrim");

      lastFocusedElement = document.activeElement;

      if (drawer) {
        drawer.removeAttribute("inert");
        drawer.setAttribute("aria-hidden", "false");
        drawer.style.visibility = "visible";
        drawer.classList.add("open");
        drawer.classList.add("on");
      }
      if (scrim) {
        scrim.classList.add("open");
        scrim.classList.add("on");
      }

      document.removeEventListener("keydown", handleDrawerKeydown);
      document.addEventListener("keydown", handleDrawerKeydown);

      this.refresh();

      setTimeout(function() {
        if (drawer) {
          var closeBtn = drawer.querySelector("#closecart");
          if (closeBtn) closeBtn.focus();
        }
      }, 50);
    },
    close: function() {
      var drawer = document.getElementById("drawer");
      var scrim = document.getElementById("scrim");

      document.removeEventListener("keydown", handleDrawerKeydown);

      if (drawer) {
        drawer.classList.remove("open");
        drawer.classList.remove("on");
        drawer.setAttribute("inert", "");
        drawer.setAttribute("aria-hidden", "true");
        setTimeout(function() {
          if (drawer && !drawer.classList.contains("open")) {
            drawer.style.visibility = "hidden";
          }
        }, 450);
      }
      if (scrim) {
        scrim.classList.remove("open");
        scrim.classList.remove("on");
      }

      if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
        lastFocusedElement.focus();
      }
    },
    refresh: function() {
      fetch("/cart.js")
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          window.HealState.cart = cart;
          var countEls = document.querySelectorAll("#cartn, .cart-count");
          countEls.forEach(function(el) { el.textContent = cart.item_count || 0; });

          var container = document.getElementById("cartitems");
          var subtotalEl = document.getElementById("cartsubtotal");
          var goalEl = document.getElementById("cartgoal");
          var shipFill = document.getElementById("cartshipfill");
          var shipBox = document.getElementById("cartshipbox");
          var pctEl = document.getElementById("cartpct");

          if (subtotalEl) {
            subtotalEl.textContent = "₹" + ((cart.total_price || 0) / 100).toLocaleString("en-IN");
          }

          if (goalEl || shipFill) {
            var total = cart.total_price || 0;
            var threshold = 49900;
            var diff = threshold - total;
            var pct = Math.min(100, Math.max(0, Math.round((total / threshold) * 100)));

            if (diff > 0) {
              if (goalEl) goalEl.innerHTML = "Add <strong>₹" + (diff / 100).toFixed(0) + "</strong> more for Free Shipping";
              if (shipBox) shipBox.classList.remove("unlocked");
            } else {
              if (goalEl) goalEl.innerHTML = "🎉 <strong>You unlocked Free Shipping!</strong>";
              if (shipBox) shipBox.classList.add("unlocked");
            }
            if (pctEl) pctEl.textContent = pct + "%";
            if (shipFill) shipFill.style.width = pct + "%";
          }

          var upsellWrap = document.getElementById("cartupsellwrap");
          if (upsellWrap) {
            var cartTotal = cart.total_price || 0;
            var cartCount = cart.item_count || 0;
            upsellWrap.style.display = (cartTotal < 49900 && cartCount > 0) ? "block" : "none";
          }

          if (container) {
            if (!cart.items || cart.items.length === 0) {
              container.innerHTML = '<div class="cart-empty" style="text-align:center;padding:36px 12px"><p style="color:var(--ink-2);margin-bottom:14px">Your cart is currently empty.</p><a href="/collections/all" class="btn sm">Start Shopping →</a></div>';
              return;
            }
            container.innerHTML = cart.items.map(function(item, idx) {
              var itemImg = item.image || (item.featured_image ? (item.featured_image.url || item.featured_image) : '');
              var imgTag = itemImg ? '<img src="' + itemImg + '" alt="' + (item.title || 'Product') + '" class="cart-row-img" width="60" height="60" loading="lazy">' : '<div class="cart-row-img" style="display:flex;align-items:center;justify-content:center;color:var(--ink-3)">📦</div>';
              return '<div class="cart-row" data-line="' + (idx + 1) + '">' +
                imgTag +
                '<div class="cart-row-info">' +
                  '<div class="cart-row-title">' + (item.product_title || item.title) + '</div>' +
                  (item.variant_title && item.variant_title !== 'Default Title' ? '<div class="cart-row-variant">' + item.variant_title + '</div>' : '') +
                  '<div class="cart-row-price">₹' + ((item.final_price || item.price) / 100).toLocaleString("en-IN") + '</div>' +
                  '<div class="cart-row-qty">' +
                    '<button type="button" class="qty-btn" onclick="HealCart.change(' + (idx + 1) + ',' + (item.quantity - 1) + ')" aria-label="Decrease quantity">−</button>' +
                    '<span>' + item.quantity + '</span>' +
                    '<button type="button" class="qty-btn" onclick="HealCart.change(' + (idx + 1) + ',' + (item.quantity + 1) + ')" aria-label="Increase quantity">+</button>' +
                  '</div>' +
                '</div>' +
                '<button type="button" class="cart-row-remove" onclick="HealCart.change(' + (idx + 1) + ', 0)" aria-label="Remove item">✕</button>' +
              '</div>';
            }).join("");
          }
        })
        .catch(function(err) {
          console.warn("Cart API error:", err);
        });
    },
    add: function(variantId, qty) {
      qty = qty || 1;
      var vid = Number(variantId) || variantId;
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ id: vid, quantity: qty })
      })
      .then(function(res) { return res.json(); })
      .then(function() {
        HealCart.open();
      })
      .catch(function(err) {
        console.error("Add to cart error:", err);
        HealCart.open();
      });
    },
    addQuickUpsell: function() {
      var btn = document.getElementById("drawer-quick-upsell-btn");
      if (btn) {
        btn.disabled = true;
        btn.textContent = "...";
      }
      fetch("/products/black-garlic.js")
        .then(function(res) { return res.json(); })
        .then(function(prod) {
          var vid = prod.variants && prod.variants[0] ? prod.variants[0].id : null;
          if (vid) {
            HealCart.add(vid, 1);
          } else {
            throw new Error("No variant");
          }
        })
        .catch(function() {
          fetch("/products/isabgol.js")
            .then(function(res) { return res.json(); })
            .then(function(prod) {
              var vid = prod.variants && prod.variants[0] ? prod.variants[0].id : null;
              if (vid) {
                HealCart.add(vid, 1);
              }
            })
            .catch(function(err) {
              console.warn("Quick upsell error:", err);
            });
        })
        .finally(function() {
          setTimeout(function() {
            if (btn) {
              btn.disabled = false;
              btn.textContent = "+ Add";
            }
          }, 1000);
        });
    },
    change: function(line, qty) {
      fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ line: line, quantity: qty })
      })
      .then(function() {
        HealCart.refresh();
      })
      .catch(function(err) {
        console.error("Cart change error:", err);
      });
    }
  };

  function initCartDrawer() {
    var drawer = document.getElementById("drawer");
    if (drawer) {
      drawer.setAttribute("inert", "");
      drawer.setAttribute("aria-hidden", "true");
      drawer.style.visibility = "hidden";
    }

    document.addEventListener("click", function(e) {
      var cartBtn = e.target.closest("#cartbtn, .cartbtn, [data-open-cart]");
      if (cartBtn) {
        e.preventDefault();
        HealCart.open();
        return;
      }
      var closeBtn = e.target.closest("#closecart, .closecart, [data-close-cart]");
      if (closeBtn) {
        e.preventDefault();
        HealCart.close();
        return;
      }
      var scrim = e.target.closest("#scrim, .scrim");
      if (scrim) {
        HealCart.close();
      }
    });

    document.addEventListener("submit", function(e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        var formData = new FormData(form);
        fetch("/cart/add.js", {
          method: "POST",
          body: formData,
          headers: { "Accept": "application/json" }
        })
        .then(function(res) { return res.json(); })
        .then(function() {
          HealCart.open();
        })
        .catch(function() {
          form.submit();
        });
      }
    });
  }

  /* ---------------- PDP Image Gallery & Tabs ---------------- */
  function initGallery() {
    document.addEventListener("click", function(e) {
      var thumb = e.target.closest("[data-shot]");
      if (!thumb) return;
      var stage = thumb.closest(".gallery") ? thumb.closest(".gallery").querySelector(".stage img") : null;
      var src = thumb.getAttribute("data-src") || (thumb.querySelector("img") ? thumb.querySelector("img").src : null);
      if (stage && src) {
        stage.src = src;
        thumb.parentElement.querySelectorAll("[data-shot]").forEach(function(b) {
          b.setAttribute("aria-pressed", b === thumb ? "true" : "false");
        });
      }
    });
  }

  /* ---------------- Stacking Product Cards Scroll Effects ---------------- */
  function initStackCards() {
    var stackItems = document.querySelectorAll(".stack-item");
    if (!stackItems || stackItems.length <= 1 || RM) return;

    var ticking = false;
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          stackItems.forEach(function(item, idx) {
            if (idx === stackItems.length - 1) return;
            var nextItem = stackItems[idx + 1];
            if (!nextItem) return;
            var panel = item.querySelector(".panel");
            if (!panel) return;

            var nextRect = nextItem.getBoundingClientRect();
            var itemRect = item.getBoundingClientRect();
            var overlap = itemRect.bottom - nextRect.top;

            if (overlap > 0 && nextRect.top < itemRect.top + itemRect.height) {
              var progress = Math.min(1, Math.max(0, overlap / (itemRect.height * 0.85)));
              var scale = 1 - progress * 0.035;
              var brightness = 1 - progress * 0.06;
              panel.style.transform = "scale(" + scale.toFixed(3) + ")";
              panel.style.filter = "brightness(" + brightness.toFixed(3) + ")";
            } else {
              panel.style.transform = "";
              panel.style.filter = "";
            }
          });
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------------- Accessible Waitlist Popup Modal ---------------- */
  var waitlistFocusedElement = null;

  function handleWaitlistKeydown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      window.HealWaitlist.close();
      return;
    }
  }

  window.HealWaitlist = {
    open: function(productTitle, productHandle) {
      var modal = document.getElementById("waitlist-modal");
      var scrim = document.getElementById("scrim");
      if (!modal) return;

      waitlistFocusedElement = document.activeElement;

      var titleEl = document.getElementById("waitlist-modal-product-title");
      var tagsInput = document.getElementById("modal-waitlist-tags");
      var bodyInput = document.getElementById("modal-waitlist-body");

      if (productTitle && titleEl) {
        titleEl.textContent = productTitle;
      }
      if (productHandle && tagsInput) {
        tagsInput.value = "waitlist, coming-soon, waitlist-" + productHandle;
      }
      if (productTitle && bodyInput) {
        bodyInput.value = "Waitlist signup for " + productTitle;
      }

      modal.removeAttribute("inert");
      modal.setAttribute("aria-hidden", "false");
      modal.classList.add("open");
      modal.classList.add("on");

      if (scrim) {
        scrim.classList.add("open");
        scrim.classList.add("on");
      }

      document.removeEventListener("keydown", handleWaitlistKeydown);
      document.addEventListener("keydown", handleWaitlistKeydown);

      setTimeout(function() {
        var emailInput = modal.querySelector("#ModalWaitlistEmail");
        if (emailInput) emailInput.focus();
      }, 50);
    },
    close: function() {
      var modal = document.getElementById("waitlist-modal");
      var scrim = document.getElementById("scrim");

      document.removeEventListener("keydown", handleWaitlistKeydown);

      if (modal) {
        modal.classList.remove("open");
        modal.classList.remove("on");
        modal.setAttribute("inert", "");
        modal.setAttribute("aria-hidden", "true");
      }

      // Only remove scrim if cart drawer is not open
      var drawer = document.getElementById("drawer");
      if (scrim && (!drawer || !drawer.classList.contains("open"))) {
        scrim.classList.remove("open");
        scrim.classList.remove("on");
      }

      if (waitlistFocusedElement && typeof waitlistFocusedElement.focus === "function") {
        waitlistFocusedElement.focus();
      }
    }
  };

  function initWaitlistModal() {
    var modal = document.getElementById("waitlist-modal");
    if (modal) {
      modal.setAttribute("inert", "");
      modal.setAttribute("aria-hidden", "true");
    }

    document.addEventListener("click", function(e) {
      var openBtn = e.target.closest("[data-open-waitlist]");
      if (openBtn) {
        e.preventDefault();
        var pTitle = openBtn.getAttribute("data-product-title") || "Black Garlic (Whole Aged Bulbs)";
        var pHandle = openBtn.getAttribute("data-product-handle") || "black-garlic";
        window.HealWaitlist.open(pTitle, pHandle);
        return;
      }

      var closeBtn = e.target.closest("#close-waitlist-modal, [data-close-waitlist]");
      if (closeBtn) {
        e.preventDefault();
        window.HealWaitlist.close();
        return;
      }

      var scrim = e.target.closest("#scrim, .scrim");
      if (scrim && modal && modal.classList.contains("open")) {
        window.HealWaitlist.close();
      }
    });

    // Check if page loaded after successful waitlist signup
    var successEl = modal ? modal.querySelector('[data-waitlist-posted="true"]') : null;
    if (successEl) {
      setTimeout(function() {
        window.HealWaitlist.open("Black Garlic", "black-garlic");
        setTimeout(function() {
          window.HealWaitlist.close();
        }, 5000);
      }, 300);
    }
  }

  /* ---------------- Initialize Everything ---------------- */
  document.addEventListener("DOMContentLoaded", function() {
    initCurrencySwitcher();
    initHeader();
    initScrollReveals();
    initHero3D();
    initHusk();
    initCartDrawer();
    initWaitlistModal();
    initGallery();
    initStackCards();
  });

})();
